/**
 * Top-level router (createBrowserRouter, data-router mode).
 *
 * Two public routes — `/auth/return` and `/auth/error` — sit OUTSIDE the AuthGate, because
 * they are what establishes (or fails to establish) the session; gating them would deadlock
 * the login round trip. `/` is a public redirect to `/site-builder` for the same reason: a bare
 * redirect needs no session, and sending it through the gate would only make the stashed deep
 * link less useful. `/branding` is the same kind of redirect — SASBND-0010 merged Login
 * Branding into the site editor, and the old path keeps working for saved links.
 *
 * Everything else is AuthGate (a live session) → AppLayout (the shell) → BrandAccessGate (the
 * principal actually admitted by the brand API). `/settings` is the one exception: it needs a
 * session and the shell, but not brand access — see the comment on the route.
 *
 * `routes` is exported so tests can mount the real composition with `createMemoryRouter`.
 */
import { Navigate, createBrowserRouter, type RouteObject } from 'react-router-dom';
import { RootLayout } from '@/routes/RootLayout';
import { AuthReturn } from '@/routes/AuthReturn';
import { AuthError } from '@/routes/AuthError';
import { EmailTemplates } from '@/routes/EmailTemplates';
import { Settings } from '@/routes/Settings';
import { AuthGate } from '@/auth/AuthGate';
import { BrandAccessGate } from '@/auth/BrandAccessGate';
import { AppLayout } from '@/components/AppLayout';

export const routes: RouteObject[] = [
  {
    path: '/',
    element: <RootLayout />,
    children: [
      { index: true, element: <Navigate to="/site-builder" replace /> },
      { path: 'branding', element: <Navigate to="/site-builder" replace /> },
      { path: 'auth/return', element: <AuthReturn /> },
      { path: 'auth/error', element: <AuthError /> },
      {
        element: <AuthGate />,
        children: [
          {
            element: <AppLayout />,
            children: [
              // Outside BrandAccessGate on purpose: appearance is a property of the console,
              // not of the tenant's brand, so a platform operator or a non-owner — neither of
              // whom the brand API admits — must still reach it from the avatar menu.
              { path: 'settings', element: <Settings /> },
              {
                element: <BrandAccessGate />,
                children: [
                  {
                    path: 'site-builder',
                    lazy: async () => {
                      const module = await import('@/features/siteBuilder/SiteBuilder');
                      return { Component: module.SiteBuilder };
                    },
                  },
                  { path: 'email-templates', element: <EmailTemplates /> },
                  {
                    path: 'email-templates/:slot/:locale',
                    lazy: async () => {
                      const module = await import('@/routes/EmailTemplateEditor');
                      return { Component: module.EmailTemplateEditor };
                    },
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
];

export const router = createBrowserRouter(routes);
